// Combat predictor — pure TS, mirrors packages/sim/src/combat/{formation,
// counter, terrain}.rs. The matrices and terrain table below are copied
// verbatim from sim so this module is the canonical client-side source.
// If a matrix changes in sim, update it here too — the test suite in
// __tests__/combatPredictor.test.ts pins these values to the Rust ones.

export type UnitClass = 'Pike' | 'LightInf' | 'HeavyInf' | 'Cavalry' | 'Ranged' | 'Siege' | 'Magic';

export type FormationType =
  | 'Line'
  | 'Standard'
  | 'Column'
  | 'SiegeColumn'
  | 'Wedge'
  | 'CavalryCharge'
  | 'Hold'
  | 'ShieldWall'
  | 'Phalanx'
  | 'Skirmish'
  | 'Ambush';

export type CombatOutcome = 'favorable' | 'even' | 'unfavorable';

export interface CombatPrediction {
  /** Class counter multiplier (Pike vs Cav = 1.5×). null if defender kind isn't a combat unit. */
  classMultiplier: number | null;
  /** Formation counter multiplier (Line vs Column = 1.3×). */
  formationMultiplier: number;
  /** Flat defense bonus from defender terrain (forest = +1, mountain = +3, etc.). */
  terrainBonus: number;
  /** Product of all three, adjusted so terrain bonus subtracts from total. */
  totalMultiplier: number;
  outcome: CombatOutcome;
}

// ── UnitKind → UnitClass map ─────────────────────────────────────────────
// Source of truth: packages/sim/src/components/units/kind.rs (UnitKind enum)
// + packages/sim/src/content/units/roster_*.rs (per-unit unit_class field).
// Sourced 2026-07-15.
const KIND_TO_UNIT_CLASS: Record<string, UnitClass | null> = {
  // Pike — anti-cavalry infantry
  pikeman: 'Pike',

  // LightInf — baseline melee
  warrior: 'LightInf',
  settler: null,
  spy: null,
  assassin: 'LightInf',
  explorer: 'LightInf',
  supply_wagon: null,
  healer: 'LightInf',
  priest: 'LightInf',
  zealot: 'LightInf',
  heretic: 'LightInf',
  inquisitor: 'LightInf',
  blood_priest: 'LightInf',
  engineer: 'LightInf',
  slaver: 'LightInf',

  // HeavyInf — armored melee
  guard: 'HeavyInf',
  crusader: 'HeavyInf',
  templar: 'HeavyInf',
  death_knight: 'HeavyInf',
  dark_templar: 'HeavyInf',
  paladin: 'HeavyInf',
  chaos_lord: 'HeavyInf',
  bronze_smith: 'HeavyInf',
  treant: 'HeavyInf',

  // Cavalry
  cavalry: 'Cavalry',

  // Ranged
  archer: 'Ranged',
  flyer: 'Ranged',

  // Siege
  siege: 'Siege',

  // Magic
  necromancer: 'Magic',
  luminarch: 'Magic',
  dreadlord: 'Magic',
  seraphim: 'Magic',
  wraith: 'Magic',
  archmage: 'Magic',
  demon: 'Magic',
  skeleton: 'Magic', // summoned undead; sim classifies as Magic
  zombie: 'Magic',

  // Ships — out of scope for land combat prediction (return null)
  trade_ship: null,
  transport_ship: null,
  warship: null,
  galleon: null,
};

export function kindToUnitClass(kind: string): UnitClass | null {
  return KIND_TO_UNIT_CLASS[kind] ?? null;
}

// ── Class counter matrix (packages/sim/src/combat/counter.rs:15-24) ──────
// Indices: 0=Pike, 1=LightInf, 2=HeavyInf, 3=Cavalry, 4=Ranged, 5=Siege, 6=Magic.
// Values are damage multipliers (×10000 basis points = 1.0).
const CLASS_COUNTER_BP: Record<UnitClass, Record<UnitClass, number>> = {
  Pike: {
    Pike: 10000,
    LightInf: 11000,
    HeavyInf: 10000,
    Cavalry: 15000,
    Ranged: 6000,
    Siege: 4000,
    Magic: 7000,
  },
  LightInf: {
    Pike: 9000,
    LightInf: 10000,
    HeavyInf: 8000,
    Cavalry: 12000,
    Ranged: 10000,
    Siege: 6000,
    Magic: 9000,
  },
  HeavyInf: {
    Pike: 10000,
    LightInf: 12000,
    HeavyInf: 10000,
    Cavalry: 9000,
    Ranged: 7000,
    Siege: 7000,
    Magic: 8000,
  },
  Cavalry: {
    Pike: 7000,
    LightInf: 13000,
    HeavyInf: 12000,
    Cavalry: 10000,
    Ranged: 14000,
    Siege: 8000,
    Magic: 9000,
  },
  Ranged: {
    Pike: 14000,
    LightInf: 8000,
    HeavyInf: 13000,
    Cavalry: 6000,
    Ranged: 10000,
    Siege: 11000,
    Magic: 9000,
  },
  Siege: {
    Pike: 6000,
    LightInf: 6000,
    HeavyInf: 7000,
    Cavalry: 5000,
    Ranged: 11000,
    Siege: 10000,
    Magic: 6000,
  },
  Magic: {
    Pike: 10000,
    LightInf: 9000,
    HeavyInf: 11000,
    Cavalry: 12000,
    Ranged: 9000,
    Siege: 14000,
    Magic: 10000,
  },
};

export function predictCounterMultiplier(attacker: UnitClass, defender: UnitClass): number {
  return CLASS_COUNTER_BP[attacker][defender] / 10000;
}

// ── Formation counter matrix (packages/sim/src/combat/formation.rs:14-23) ─
// Indices: 0=Line, 1=Column, 2=Wedge, 3=Hold, 4=Phalanx, 5=Skirmish.
type CoreFormation = 'Line' | 'Column' | 'Wedge' | 'Hold' | 'Phalanx' | 'Skirmish';

function coreFormation(f: FormationType): CoreFormation {
  switch (f) {
    case 'Line':
    case 'Standard':
      return 'Line';
    case 'Column':
    case 'SiegeColumn':
      return 'Column';
    case 'Wedge':
    case 'CavalryCharge':
      return 'Wedge';
    case 'Hold':
    case 'ShieldWall':
      return 'Hold';
    case 'Phalanx':
      return 'Phalanx';
    case 'Skirmish':
    case 'Ambush':
      return 'Skirmish';
  }
}

const FORMATION_COUNTER_BP: Record<CoreFormation, Record<CoreFormation, number>> = {
  // Line   Col   Wedge  Hold  Phal  Skirm
  Line: { Line: 10000, Column: 13000, Wedge: 8000, Hold: 10000, Phalanx: 7000, Skirmish: 12000 },
  Column: { Line: 7000, Column: 10000, Wedge: 13000, Hold: 12000, Phalanx: 8000, Skirmish: 10000 },
  Wedge: { Line: 13000, Column: 7000, Wedge: 10000, Hold: 12000, Phalanx: 8000, Skirmish: 9000 },
  Hold: { Line: 10000, Column: 8000, Wedge: 7000, Hold: 10000, Phalanx: 12000, Skirmish: 13000 },
  Phalanx: { Line: 13000, Column: 12000, Wedge: 10000, Hold: 8000, Phalanx: 10000, Skirmish: 6000 },
  Skirmish: {
    Line: 8000,
    Column: 10000,
    Wedge: 12000,
    Hold: 7000,
    Phalanx: 13000,
    Skirmish: 10000,
  },
};

export function predictFormationMultiplier(
  attacker: FormationType,
  defender: FormationType,
): number {
  return FORMATION_COUNTER_BP[coreFormation(attacker)][coreFormation(defender)] / 10000;
}

// ── Terrain defense (packages/sim/src/combat/terrain.rs:6-19) ────────────
// Returns the flat defense bonus the defender gains from their hex.
const TERRAIN_DEFENSE: Record<string, number> = {
  forest: 1,
  hills: 2,
  mountain: 3,
  river: 1,
  plains: 0,
  swamp: 0,
  road: 0,
  city: 2,
  desert: 0,
  tundra: 0,
  coast: 0,
  ocean: 0,
};

export function predictTerrainBonus(terrain: string): number {
  return TERRAIN_DEFENSE[terrain.toLowerCase()] ?? 0;
}

// ── Aggregator ───────────────────────────────────────────────────────────

export interface PredictCombatInput {
  attackerKind: string;
  attackerFormation: FormationType;
  defenderKind: string;
  defenderFormation: FormationType;
  defenderTerrain: string;
  attackerHp: number;
  attackerHpMax: number;
  defenderHp: number;
  defenderHpMax: number;
}

const FAVORABLE_THRESHOLD = 1.2;
const UNFAVORABLE_THRESHOLD = 0.8;

export function predictCombatOutcome(input: PredictCombatInput): CombatPrediction {
  const attackerClass = kindToUnitClass(input.attackerKind);
  const defenderClass = kindToUnitClass(input.defenderKind);
  const classMultiplier =
    attackerClass !== null && defenderClass !== null
      ? predictCounterMultiplier(attackerClass, defenderClass)
      : null;
  const formationMultiplier = predictFormationMultiplier(
    input.attackerFormation,
    input.defenderFormation,
  );
  const terrainBonus = predictTerrainBonus(input.defenderTerrain);

  // Total = class × formation, with terrain subtracted as a 5% penalty per bonus point
  // (matches the sim's "defense_bonus = flat bonus to defense roll" model).
  const baseProduct = (classMultiplier ?? 0) * formationMultiplier;
  const totalMultiplier = classMultiplier === null ? 0 : baseProduct * (1 - 0.05 * terrainBonus);

  let outcome: CombatOutcome;
  if (classMultiplier === null) {
    outcome = 'unfavorable';
  } else if (totalMultiplier > FAVORABLE_THRESHOLD) {
    outcome = 'favorable';
  } else if (totalMultiplier < UNFAVORABLE_THRESHOLD) {
    outcome = 'unfavorable';
  } else {
    outcome = 'even';
  }

  return {
    classMultiplier,
    formationMultiplier,
    terrainBonus,
    totalMultiplier,
    outcome,
  };
}
